/**
 * Parses a hex colour string (`#RRGGBB` or `#RGB`) into its 0–255 channel
 * values. Shared by the Entri theme bridge (shadow-colour RGB triplets) and
 * the brand document's WCAG contrast math.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = hex.trim().slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  return {
    r: Number.parseInt(h.slice(0, 2), 16),
    g: Number.parseInt(h.slice(2, 4), 16),
    b: Number.parseInt(h.slice(4, 6), 16),
  };
}

/**
 * Resolves any CSS color to its sRGB channels (0–255, alpha 0–1) by painting it onto a 1x1 canvas.
 * Pluma's tokens are authored in oklch, which code outside our stylesheets can't always read: the
 * Maps API, and the Entri and Stripe iframes. Returns `null` when the browser can't parse the color
 * or there is no canvas (jsdom).
 */
export function resolveRgb(color: string): { r: number; g: number; b: number; a: number } | null {
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context == null) return null;
  // An unparseable color leaves fillStyle as it was, so start from a sentinel to tell the two apart.
  const sentinel = '#010203';
  context.fillStyle = sentinel;
  context.fillStyle = color;
  if (context.fillStyle === sentinel && color.trim().toLowerCase() !== sentinel) return null;
  context.fillRect(0, 0, 1, 1);
  const [r = 0, g = 0, b = 0, alpha = 255] = context.getImageData(0, 0, 1, 1).data;
  return { r, g, b, a: Math.round((alpha / 255) * 1000) / 1000 };
}

/** `color` as an `rgb()` / `rgba()` string, or unchanged when it can't be resolved. */
export function toRgbColor(color: string): string {
  const rgb = resolveRgb(color);
  if (rgb == null) return color;
  return rgb.a === 1 ? `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` : `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${rgb.a})`;
}
